import { SelectionStoreConfig, SelectionStoreMode } from '../types';

export class SelectionStore<GridItemType> {
  private selectedItems: Array<GridItemType> | Array<string> = [];

  constructor(private config: SelectionStoreConfig) {}

  public get storeData(): Array<GridItemType | string> {
    return this.selectedItems;
  }

  public get count(): number {
    return this.selectedItems.length;
  }

  public add(itemsArray: Array<GridItemType>): void {
    if (this.selectedItems.length + itemsArray.length <= this.config.maxNumOfItems) {
      if (this.config.functionalMode === SelectionStoreMode.IDS) {
        this.addIds(itemsArray);
      } else if (this.config.functionalMode === SelectionStoreMode.RECORDS) {
        this.addRecords(itemsArray);
      } else {
        throw new Error('Functional mode not supported in SelectionStore. Items were not added in store.');
      }
    } else {
      throw new Error('Maximum number of items in SelectionStore has been reached. Items were not added in store.');
    }
  }

  public remove(itemsArray: Array<GridItemType>): void {
    const removeIds = itemsArray.map(item => item[this.config.uniqueIdentifier]);

    if (this.config.functionalMode === SelectionStoreMode.IDS) {
      this.selectedItems = (this.selectedItems as Array<string>).filter(value => !removeIds.includes(value));
      return;
    }

    if (this.config.functionalMode === SelectionStoreMode.RECORDS) {
      const toDelete = new Set(removeIds);
      this.selectedItems = (this.selectedItems as Array<GridItemType>).filter(
        obj => !toDelete.has(obj[this.config.uniqueIdentifier])
      );
      return;
    }

    throw new Error('Functional mode not supported in SelectionStore. Items were not removed from store.');
  }

  public getItemsIntersection(itemsArray: Array<GridItemType>): Array<GridItemType> {
    if (
      !(
        this.config.functionalMode === SelectionStoreMode.IDS ||
        this.config.functionalMode === SelectionStoreMode.RECORDS
      )
    ) {
      throw new Error('Functional mode not supported in SelectionStore. Items could not be compared.');
    }

    const returnedSelectedItems: Array<GridItemType> = [];
    itemsArray.forEach(item => {
      const id = item[this.config.uniqueIdentifier];
      if (this.selectedItems.includes(id) || this.isIdInRecords(id)) {
        returnedSelectedItems.push(item);
      }
    });
    return returnedSelectedItems;
  }

  public removeAll(): void {
    this.selectedItems = [];
  }

  private addRecords(items: Array<GridItemType>): void {
    if (this.selectedItems.length === 0) {
      items.forEach(item => {
        (this.selectedItems as Array<GridItemType>).push(item);
      });
      return;
    }

    items.forEach(sourceElement => {
      const targetElement = (this.selectedItems as Array<GridItemType>).find(element => {
        return sourceElement[this.config.uniqueIdentifier] === element[this.config.uniqueIdentifier];
      });
      targetElement
        ? Object.assign(targetElement, sourceElement)
        : (this.selectedItems as Array<GridItemType>).push(sourceElement);
    });
  }

  private addIds(itemsArray: Array<GridItemType>): void {
    itemsArray.forEach(item => {
      const id = item[this.config.uniqueIdentifier];
      if (!this.selectedItems.includes(id)) {
        this.selectedItems.push(id);
      }
    });
  }

  private isIdInRecords(id: any): boolean {
    return (
      (this.selectedItems as Array<GridItemType>).filter(record => record[this.config.uniqueIdentifier] === id).length >
      0
    );
  }
}
